import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Send, Check, CheckCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatView = ({ conversationId, partnerName, accentColor = '#467322' }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const intervalRef = useRef(null);

  const fetchMessages = React.useCallback(async () => {
    if (!conversationId) return;
    try {
      const res = await axios.get(`${API}/messages/conversations/${conversationId}/messages`);
      setMessages(res.data);
    } catch (error) { console.error('Failed to fetch messages:', error); }
  }, [conversationId]);

  useEffect(() => {
    fetchMessages();
    intervalRef.current = setInterval(fetchMessages, 15000);
    return () => clearInterval(intervalRef.current);
  }, [fetchMessages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/messages/conversations/${conversationId}/messages`, { text: text.trim() });
      setMessages(prev => [...prev, res.data]);
      setText('');
    } catch (error) { console.error('Failed to send message:', error); } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateSeparator = (dateStr) => {
    const d = new Date(dateStr);
    const today = new Date();
    if (d.toDateString() === today.toDateString()) return 'Hoy';
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Ayer';
    return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
  };

  const getDateGroups = () => {
    const groups = [];
    let lastDate = '';
    messages.forEach(m => {
      const dateKey = new Date(m.created_at).toDateString();
      if (dateKey !== lastDate) {
        groups.push({ type: 'date', label: formatDateSeparator(m.created_at), key: `date-${dateKey}` });
        lastDate = dateKey;
      }
      groups.push({ type: 'message', data: m, key: m.id });
    });
    return groups;
  };

  return (
    <div className="flex flex-col h-full" data-testid="chat-view">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-1">
        {messages.length === 0 && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3">
                <Send size={24} className="text-gray-300" />
              </div>
              <p className="text-sm text-gray-400">Envía el primer mensaje</p>
            </div>
          </div>
        )}
        {getDateGroups().map(item => {
          if (item.type === 'date') {
            return (
              <div key={item.key} className="flex justify-center my-3">
                <span className="text-[10px] font-semibold text-gray-400 bg-gray-100 px-3 py-1 rounded-full">
                  {item.label}
                </span>
              </div>
            );
          }
          const m = item.data;
          const isMine = m.sender_id === user?.id;
          return (
            <div key={item.key} className={`flex ${isMine ? 'justify-end' : 'justify-start'} mb-1`}>
              <div className={`max-w-[75%] px-3.5 py-2 rounded-2xl ${
                isMine
                  ? 'rounded-br-md text-white'
                  : 'bg-white border border-gray-100 rounded-bl-md text-gray-800'
              }`} style={isMine ? { background: accentColor } : {}}>
                {!isMine && (
                  <p className="text-[10px] font-bold mb-0.5" style={{ color: accentColor }}>{m.sender_name}</p>
                )}
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{m.text}</p>
                <div className={`flex items-center justify-end gap-1 mt-0.5 ${isMine ? 'text-white/60' : 'text-gray-400'}`}>
                  <span className="text-[9px]">{formatTime(m.created_at)}</span>
                  {isMine && (m.read ? <CheckCheck size={12} /> : <Check size={12} />)}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="p-3 border-t border-gray-100 bg-white/80 backdrop-blur-lg">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Escribe un mensaje..."
            className="flex-1 bg-gray-50 border border-gray-100 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-gray-200 focus:ring-2 focus:ring-gray-100 transition-all"
            data-testid="chat-input"
          />
          <button
            type="submit"
            disabled={!text.trim() || sending}
            className="w-10 h-10 rounded-full flex items-center justify-center text-white shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-40 disabled:shadow-none disabled:translate-y-0 flex-shrink-0"
            style={{ background: accentColor }}
            data-testid="chat-send-btn"
          >
            <Send size={16} />
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatView;
